import { ButtonType } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { Formatters, Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { MultilineTextField } from '../../common/react/components/MultilineTextField';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AuthenticationProviderSelector } from '../../settings/siteAuthSettings/directives/AuthenticationProviderSelector';
import { AuthenticationProviderSelectorProperties, getDefaultAuthenticationProviderSelectorProperties } from '../../settings/siteAuthSettings/model/AuthenticationProviderSelectorProperties';
import { IAuthenticationProvider } from '../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { EmailValidationHelper } from '../../ts/helpers/EmailValidationHelper';
import { Set } from '../../ts/models/Set';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IUserActionResult, UserActionResultCode } from '../../ts/ServerApi/types/IUserActionResult';
import { UserActionNotification } from '../services/notifications/UserActionNotification';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteUsers } from '../services/resources/SiteUsers';


export interface AddExternalSiteUsersDialogState {
  readonly siteRole: string;
  readonly busy: boolean;
  readonly userInput: string;
  readonly invalidEmails: string[];
  readonly authenticationProviderSelectorProperties: AuthenticationProviderSelectorProperties;
}

export interface AddExternalSiteUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly availableQuota: number;
}

export function isQuotaEnabled(availableQuota: number, isServerDomainFamilyExternal: boolean,
  hasSubscriptionTieredLicensing: boolean): boolean {
 if (isServerDomainFamilyExternal && hasSubscriptionTieredLicensing) {
   return false;
 }
 return _.isNumber(availableQuota);
}

const splitEmails = (emailText: string): string[] => _.chain(emailText.split(/[;,\r\n]+/))
                                                    .map((email: string) => email.trim())
                                                    .compact() // Remove empty strings
                                                    .unique()  // Remove dupes
                                                    .value();
export class AddExternalSiteUsersDialog extends React.Component<AddExternalSiteUsersDialogProps, AddExternalSiteUsersDialogState> {
  private siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private server: SessionManagementService = getSessionManagementServiceInstance();;
  private userActionNotification: UserActionNotification = UserActionNotification.instance;
  public constructor(props: AddExternalSiteUsersDialogProps) {
    super(props);
    this.state = {
      siteRole:
        this.props.availableQuota <= 0 ? SiteRoles.Unlicensed : DefaultSiteRole,
      busy: false,
      userInput: '',
      invalidEmails: [],
      authenticationProviderSelectorProperties: getDefaultAuthenticationProviderSelectorProperties()
    };
  }

  private readonly importUsersCallback = () => {
    let invalidEmails = this.getInvalidEmails(this.state.userInput);
    this.setState({invalidEmails});
    if(invalidEmails.length === 0){
      this.importUsers();
    }
  }

  private readonly handleChangeCallback = (userInput: string) => {
    this.setState({userInput});
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(siteRole);
  }

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.onSiteRoleSelected(siteRoleOption);
  }

  private siteRoleOptions = this.server.isSessionServerAdmin() === true ? this.siteRolesOptionResource.getAllSiteRoleOptions() : this.siteRolesOptionResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);

  public render() {
    return (
      <div className="tb-dialog-external-users">
        <div className="tb-dialog-title">{Messages.addExternalUsersTitle()}</div>
        {this.renderAuthenticationProviderSelector()}
        <div>{Messages.addExternalUsersDesc()}</div>
        <div className="tb-dialog-body-control tb-multiline-text-holder">
          <MultilineTextField
            handleChange={this.handleChangeCallback}
            disabled={this.state.busy}
            placeholder={Messages.addExternalUsersPlaceholder()}
          />
        </div>
        {this.renderQuotaMessageIfApplicable()}
        {this.renderInvalidEmailsIfApplicable()}
        <div className="tb-row">
          <span className="tb-row-height-box tb-margin-right">
            {Messages.siteRole()}
          </span>
          <span className="tb-row-with-icon-on-right">
            <SiteRolePickerMenu
              siteRoleOptions={this.siteRoleOptions}
              getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
              onOptionSelected={this.onOptionSelectedCallback}
              selectedRole={this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(this.state.siteRole)}
              isDisabled={this.state.busy}
            />
          </span>
        </div>
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            showSpinner={false}
            clicked={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            title={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Go}
              showSpinner={this.state.busy}
              clicked={this.importUsersCallback}
              testId={'confirm-action-dialog-submit'}
              title={Messages.addUsersDialogSubmit()}
              disabled={this.state.busy || this.state.userInput.length === 0}
            />
          </span>
        </div>
      </div>
    );
  }

  public renderQuotaMessageIfApplicable() {
    if (isQuotaEnabled(this.props.availableQuota,
      ServerSettingsUnauthenticated.isServerDomainFamilyExternal(),
      this.server.hasSubscriptionTieredLicensing())) {
      return (
        <div className={'tb-available-quota tb-row'}>
          {Messages.availableQuota({NUM_USERS: this.props.availableQuota, NUM_USERS_LOC: Formatters.formatCount(this.props.availableQuota)})}
        </div>
      );
    }
    return null;
  }

  private renderInvalidEmailsIfApplicable() {
    if (this.state.invalidEmails && this.state.invalidEmails.length > 0) {
      let translatedInvalidEmails = this.state.invalidEmails.map((invalidEmail: string) => {
        return Messages.addExternalUsersInvalidEmail({ EMAIL: invalidEmail });
      });
      return (
        <div className={'tb-dialog-warnings-list'}>
          {translatedInvalidEmails.map(function(translatedInvalidEmail: string, index) {
            return (
              <div key={index} className={'tb-dialog-invalid-email-warning'}>
                {translatedInvalidEmail}
              </div>);
          })}
        </div>
        );
    }
    return null;
  }

  private getInvalidEmails(userInput: string){
    let emails = splitEmails(userInput);
    let invalidEmails = EmailValidationHelper.getInvalidEmails(emails);
    return invalidEmails;
  }

  private renderAuthenticationProviderSelector() {
    let currentModel = this.state.authenticationProviderSelectorProperties;
    const onProviderChange = (provider: IAuthenticationProvider) => {
      this.setState({
        authenticationProviderSelectorProperties: {...currentModel, authenticationProvider: provider}
      });
    };
    const onProviderIdsChange = (ids: string[]) => {
      this.setState({
        authenticationProviderSelectorProperties: {...currentModel, authenticationProviderIds: ids}
      });
    };
    const onAuthSettingChange = (authSetting: string) => {
      this.setState({
        authenticationProviderSelectorProperties: {...currentModel, authSetting: authSetting}
      });
    };
    return (
      <AuthenticationProviderSelector {...this.state.authenticationProviderSelectorProperties} onProviderChange={onProviderChange} onProviderIdsChange={onProviderIdsChange} onAuthSettingChange={onAuthSettingChange}/>
    );
  }

  private onSiteRoleSelected(siteRoleOption: SiteRoleOption): void {
    this.setState({siteRole: siteRoleOption.role});
  }

  public importUsers(): Promise<any> {
    this.setState({busy: true});
    let emails = splitEmails(this.state.userInput);
    return SiteUsers.instance
      .importExternalUsers(emails, this.state.siteRole, this.state.authenticationProviderSelectorProperties.authenticationProvider.id)
      .then((result: IUserActionResult) => {
        // DO NOT SET STATE in this then clause
        this.userActionNotification.notify({
          users: Set.fromIdItemArray(result.users),
          results: result.results,
          errors: result.errors,
          successCode: UserActionResultCode.siteUserCreated,
          siteRole: this.state.siteRole
        });
        this.props.close({});
      });
  }

}
